import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PlusIcon, PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import { ErrorBanner, PrimaryButton } from "../../components/crm/Form";
import RuleFormModal from "../../components/monitoring/RuleFormModal";
import { formatApiError } from "../../lib/api";
import {
  ALERT_TYPE_LABEL,
  VENDOR_LABEL,
  deleteRule,
  listRules,
  updateRule,
} from "../../lib/monitoring";

export default function MonitoringRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await listRules();
      setRules(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleActive = async (rule) => {
    try {
      await updateRule(rule.id, { is_active: !rule.is_active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await deleteRule(rule.id);
      toast.success("Rule deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title="Alert Rules"
          subtitle="Automate triage when monitoring alerts come in"
          actions={
            <PrimaryButton onClick={() => setCreating(true)} data-testid="rules-new-btn">
              <PlusIcon size={16} className="mr-1.5" /> New rule
            </PrimaryButton>
          }
        />

        <ErrorBanner message={err} />

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-4 py-2.5">Rule</th>
                <th className="px-4 py-2.5">Conditions</th>
                <th className="px-4 py-2.5">Actions</th>
                <th className="px-4 py-2.5 w-20">Active</th>
                <th className="px-4 py-2.5 w-24 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-500 text-sm">
                    Loading rules…
                  </td>
                </tr>
              )}
              {!loading && rules.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-500 text-sm">
                    No rules yet.
                  </td>
                </tr>
              )}
              {rules.map((r) => (
                <tr key={r.id} data-testid={`rule-row-${r.id}`} className="border-t border-slate-200">
                  <td className="px-4 py-2.5 align-top">
                    <div className="text-sm font-semibold text-slate-900">{r.name}</div>
                  </td>
                  <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                    {summarizeConditions(r.conditions)}
                  </td>
                  <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                    {summarizeActions(r.actions)}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <button
                      type="button"
                      onClick={() => toggleActive(r)}
                      data-testid={`rule-toggle-${r.id}`}
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide transition ${
                        r.is_active
                          ? "bg-emerald-100 text-emerald-900 border-emerald-300 hover:bg-emerald-200"
                          : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200"
                      }`}
                    >
                      {r.is_active ? "On" : "Off"}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 align-top text-right">
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      data-testid={`rule-edit-${r.id}`}
                      className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 mr-1"
                      aria-label="Edit"
                    >
                      <PencilSimpleIcon size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(r)}
                      data-testid={`rule-delete-${r.id}`}
                      className="rounded p-1 text-slate-500 hover:bg-red-100 hover:text-red-700"
                      aria-label="Delete"
                    >
                      <TrashIcon size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <RuleFormModal
        open={creating || !!editing}
        rule={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={load}
      />
    </Layout>
  );
}

function summarizeConditions(c) {
  if (!c) return "match all";
  const parts = [];
  if (c.severity_in?.length) parts.push(`severity: ${c.severity_in.join(", ")}`);
  if (c.alert_type_in?.length)
    parts.push(
      `types: ${c.alert_type_in.map((t) => ALERT_TYPE_LABEL[t] || t).join(", ")}`
    );
  if (c.vendor_in?.length)
    parts.push(`vendors: ${c.vendor_in.map((v) => VENDOR_LABEL[v] || v).join(", ")}`);
  return parts.length ? parts.join(" · ") : "match all";
}

function summarizeActions(a) {
  if (!a) return "—";
  const parts = [];
  if (a.create_service_request) parts.push("Auto-SR");
  if (a.notify_ops) parts.push("Notify ops");
  return parts.length ? parts.join(" · ") : "—";
}
